import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
  upsertOrGroup,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Input } from "@/components/ui/input";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

export function BranchesFilter() {
  const nav = useNavigate({ from: "/app/branches" });
  const search = useSearch({ from: "/app/branches" }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const searchFilter = findCondition(filters, "code");
  const nameFilter = findCondition(filters, "name");
  const isActiveFilter = findCondition(filters, "isActive");

  const [searchValue, setSearchValue] = useState<string>(
    (searchFilter?.value as string) || (nameFilter?.value as string) || "",
  );

  useEffect(() => {
    const codeVal = searchFilter?.value as string;
    const nameVal = nameFilter?.value as string;
    setSearchValue(codeVal || nameVal || "");
  }, [searchFilter?.value, nameFilter?.value]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertOrGroup(filters, [
        { field: "code", op: "contains", value: val },
        { field: "name", op: "contains", value: val },
      ]);
    } else {
      nextFilters = removeConditions(filters, "code");
      nextFilters = removeConditions(nextFilters ?? [], "name");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const isActiveValue =
    typeof isActiveFilter?.value === "boolean"
      ? String(isActiveFilter.value)
      : "";
  const hasFilter =
    !!searchFilter?.value || !!nameFilter?.value || isActiveValue !== "";

  const clearFilters = () => {
    setSearchValue("");
    nav({
      search: {
        ...search,
        offset: 0,
        filters: undefined,
      },
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 pb-2">
      <Input
        placeholder="ຄົ້ນຫາຕາມລະຫັດ ຫຼື ຊື່ສາຂາ..."
        defaultValue={searchValue}
        onChange={(e) => debounced(e.target.value)}
        className="max-w-xs"
      />
      <SimpleSelect
        placeholder="ສະຖານະ"
        value={isActiveValue ?? "all"}
        onValueChange={(v) => {
          const next =
            v === "all"
              ? removeConditions(filters, "isActive")
              : upsertCondition(filters, {
                  field: "isActive",
                  op: "eq",
                  value: v === "true",
                });
          nav({
            search: {
              ...search,
              offset: 0,
              filters: next?.length ? next : undefined,
            },
          });
        }}
        options={[
          { value: "all", label: "ທຸກສະຖານະ" },
          { value: "true", label: "ເປີດ" },
          { value: "false", label: "ປິດ" },
        ]}
      />
      {hasFilter && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <XIcon className="h-4 w-4" />
          ລ້າງຕົວກອງ
        </button>
      )}
    </div>
  );
}
